import { Inject, Injectable } from '@angular/core';
import { UserActionItem } from 'app/modules/today/models/userActionItem.model';
import { State, Action, StateContext, Selector, Store, createSelector, Select } from '@ngxs/store';
import {
  ActionItemTaskCompleted,
  GetUserActionItemDetails,
  GetUserActionItemTypes,
  GetUserActionItems,
  SetUserActionItemsUser,
  SetSelectedTabByName,
  SetTodayTabs,
  ViewDocumentReviewTaskByDocId,
  SetUserActionItemDetails,
  SubscribeToCompletedUserActionItems,
  UnsubscribeFromCompletedUserActionItems,
  UnsetActiveUserActionItem,
  SendToFundingDesk,
  GetKickbackCount,
  ClearKickbackCount, MarkActionItemCompleted,
} from './user-action-items.action';
import { catchError, take, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { UserActionItemType } from 'ng2-app/modules/today/models/userActionItemType.model';
import { UserActionItemsService } from 'ng2-app/modules/today/services/userActionItems.service';
import { FunnelUsers } from 'ng2-app/modules/funnel/models/funnel-users';
import { LenderUser } from 'ng2-app/models/dto/lender-user.model';
import { EventBusService } from 'ng2-app/modules/shared/services/event-bus.service';
import { GetDocReviewTaskErrorEvent } from 'ng2-app/modules/shared/services/event-types';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { GetTodayBadgeCounts } from './today-badge-counts/today-badge-counts.action';
import { environment } from 'environments/environment';
import { UserActionItemDetailState } from './user-action-item-detail.state';
import { WorkflowService } from 'app/modules/borrower/services/workflow.service';
import Rollbar from 'rollbar';
import { RollbarService } from 'app/rollbar';

export interface UserActionItemTypes {
  today: UserActionItemType[];
  requests: UserActionItemType[];
}

export class UserActionItemsStateModel {
  user?: FunnelUsers | LenderUser;
  userActionItems: UserActionItem[];
  userActionItemTypes: {
    today: UserActionItemType[];
    requests: UserActionItemType[];
  };
  selectedTabIndex: number;
  tabs: Array<string>;
  loading: boolean;
  error: any;
  total: number;
  page: number;
  snapshotState?: any;
  kickbackCount: number;
}

@State<UserActionItemsStateModel>( {
  name: 'UserActionItems',
  defaults: {
    selectedTabIndex: 0,
    tabs: [],
    userActionItems: [],
    userActionItemTypes: {
      today: [],
      requests: [],
    },
    loading: false,
    error: null,
    page: 0,
    total: 0,
    kickbackCount: 0,
  },
} )
@Injectable()
export class UserActionItemsState {

  @Selector()
  static userId( state: UserActionItemsStateModel ) {
    return state.user?.id;
  }

  @Selector()
  static user( state: UserActionItemsStateModel ) {
    return state.user;
  }

  @Selector()
  static userActionItemTypes( state: UserActionItemsStateModel ) {
    return state.userActionItemTypes;
  }

  @Selector()
  static userActionItemTypesByTab( tabName: string ) {
    return createSelector([UserActionItemsStateModel], ({UserActionItems}) => UserActionItems.userActionItemTypes[tabName] || []);
  }

  @Selector()
  static userActionItems( state: UserActionItemsStateModel ) {
    return state.userActionItems;
  }

  @Selector()
  static loading( state: UserActionItemsStateModel ) {
    return state.loading;
  }

  @Selector()
  static total( state: UserActionItemsStateModel ) {
    return state.total;
  }

  @Selector()
  static selectedTabIndex( state: UserActionItemsStateModel ) {
    return state.selectedTabIndex;
  }

  @Selector()
  static outstandingKickbacks( state: UserActionItemsStateModel ) {
    return state.kickbackCount > 1;
  }

  @Select(UserActionItemDetailState.activeActionItem) activeActionItem$: Observable<UserActionItem>;

  constructor(
    private actionItemsService: UserActionItemsService,
    private store: Store,
    private eventBusService: EventBusService,
    private pusherService: PusherService,
    private workflowService: WorkflowService,
    @Inject(RollbarService) private rollbar: Rollbar
  ) { }

  @Action( SetTodayTabs )
  setTabs(
    { patchState }: StateContext<UserActionItemsStateModel>,
    { tabs }: SetTodayTabs
  ) {
    patchState({tabs});
  }

  @Action( SetSelectedTabByName )
  setSelectedTabByName(
    { getState, patchState }: StateContext<UserActionItemsStateModel>,
    { tabName }: SetSelectedTabByName
  ) {
    const tabs = getState().tabs,
      selectedTabIndex = tabs?.indexOf(tabName);

    if(selectedTabIndex !== -1) {
      patchState( { selectedTabIndex } );
    }

  }

  @Action( SetUserActionItemsUser )
  setUserActionItemsUserId(
    { patchState }: StateContext<UserActionItemsStateModel>,
    { user }: SetUserActionItemsUser
  ) {
    patchState( {
      user
    } );

  }

  @Action( GetUserActionItems )
  getUserActionItems(
    { getState, patchState }: StateContext<UserActionItemsStateModel>,
    { reset, types, sortDirection, completed }: GetUserActionItems
  ) {

    const current = getState();

    const prePatch = {
      userActionItems: current.userActionItems,
      loading: true,
    };


    if ( reset ) {
      prePatch.userActionItems = [];
    }

    patchState( prePatch );

    if ( current.user?.id ) {
      const page = reset ? 1 : ++current.page;
      return this.actionItemsService
        .getUserActionItems( current.user.id, page, types, sortDirection, completed )
        .pipe(
          tap( ( response ) => {
            const currentItems = getState().userActionItems;
            const userActionItems = reset ? response.data :
              sortDirection === 'desc' ? [ ...currentItems, ...response.data ] :
                [ ...response.data, ...currentItems ];

            const firstActionItem = userActionItems[0];

            patchState( {
              loading: false,
              page: response.current_page,
              total: response.total,
              userActionItems,
            } );

            if ( reset && firstActionItem ) {
              this.store.dispatch( new GetUserActionItemDetails( firstActionItem.id ) );
            }

          } ),
          catchError( ( error ) => {
            patchState( {
              userActionItems: [],
              loading: false,
              error,
            } );
            return of( '' );
          } )
        );
    }
  }

  @Action( GetUserActionItemTypes )
  getUserActionItemTypes(
    { patchState }: StateContext<UserActionItemsStateModel>,
  ) {
    return this.actionItemsService
      .getUserActionItemTypes()
      .pipe(
        tap( userActionItemTypes => {
          patchState( { userActionItemTypes } );
        } ),
        catchError( ( error ) => {
          patchState( {
            error
          } );
          return of( '' );
        } )
      );
  }

  @Action( ActionItemTaskCompleted )
  actionItemTaskCompleted(
    { getState, patchState }: StateContext<UserActionItemsStateModel>,
    { taskId, borrowerKickback }: ActionItemTaskCompleted
  ) {
    const userActionItems = getState().userActionItems.filter( actionItem => actionItem.modelId !== taskId );
    const firstActionItem = userActionItems[0] || null;
    if ( firstActionItem ) {
      this.store.dispatch( new GetUserActionItemDetails( firstActionItem.id ) );
    }

    patchState({userActionItems});

    // Auto-resubmission to funding desk
    if (borrowerKickback?.kickback) {
      const additionalKickbacks = this.multipleExistingKickbacksForBorrower(userActionItems, borrowerKickback.borrowerId);
      if (additionalKickbacks > 0) {
        this.eventBusService.publish(
          new this.eventBusService.types.KickbackEvent(
            true,
            `{ ${additionalKickbacks} } outstanding request(s) for ${borrowerKickback.borrowerName}.`)
        );
      } else {
        this.store.dispatch(new SendToFundingDesk(borrowerKickback));
      }
    }
  }

  @Action(SendToFundingDesk)
  sendToFundingDesk({ }: StateContext<UserActionItemsStateModel>, { borrower }: SendToFundingDesk) {
    this.workflowService.sendToFundingDesk(borrower.borrowerId).pipe(
      catchError((error) => {
        this.rollbar.error('Send to funding desk error.  ', error);
        this.eventBusService.publish(
          new this.eventBusService.types.KickbackEvent(
            false, `There was an error auto-resubmitting ${borrower.borrowerName}'s application.`
          )
        );
        return of();
      }),
      tap((result: { success: boolean, borrowerName: string }) => {
        if (result) {
          const borrowerName = result.borrowerName ?? 'of this borrower\'s';
          this.eventBusService.publish(
            new this.eventBusService.types.KickbackEvent(
              true, `All ${borrowerName} requests completed; lead submitted to funding desk.`
            )
          );
        }
      })
    ).subscribe();
  }

  @Action(GetKickbackCount)
  getKickbackCount({ getState, patchState }: StateContext<UserActionItemsStateModel>, { borrowerId }: GetKickbackCount) {
    const userActionItems = getState().userActionItems;
    const kickbackCount = this.multipleExistingKickbacksForBorrower(userActionItems, borrowerId);
    patchState({ kickbackCount });
  }

  @Action(ClearKickbackCount)
  clearKickbackCount({ patchState }: StateContext<UserActionItemsStateModel>, { }: ClearKickbackCount) {
    patchState({ kickbackCount: 0 });
  }

  @Action( ViewDocumentReviewTaskByDocId )
  viewDocumentReviewTaskByDocId(
    { getState, patchState }: StateContext<UserActionItemsStateModel>,
    { documentId }: ViewDocumentReviewTaskByDocId
  ) {
    const state = getState();
    const actionItem = state.userActionItems.filter( ai => ai.type === 'Document Upload Review' && ai.modelId === documentId)[0];

    if(actionItem) {
      return this.store.dispatch( new GetUserActionItemDetails(actionItem.id) );
    }

    patchState({
      loading: true,
      snapshotState: state,
    });

    return this.actionItemsService.getUserActionItemByDocId(documentId).pipe(
      tap( userActionItem => {
        patchState({
          loading: false,
          userActionItems: [...state.userActionItems, userActionItem],
        });
        this.store.dispatch( new SetUserActionItemDetails(userActionItem) );
        this.store.dispatch( new SetSelectedTabByName('today'));
      }),
      catchError( e => {
        this.eventBusService.publish( new GetDocReviewTaskErrorEvent('No User Action Item Found for document') );
        patchState({
          loading: false,
          error: e,
        });
        return of(e);
      })
    );
  }

  @Action(UnsubscribeFromCompletedUserActionItems)
  unsubscribeFromCompletedUserActionItems(
    {}: StateContext<UserActionItemsStateModel>,
    { lenderUser }: UnsubscribeFromCompletedUserActionItems
  ) {
    this.pusherService.unsubscribe(`private-lender-user-${lenderUser.id}-today-tab-channel`);
  }

  @Action(SubscribeToCompletedUserActionItems)
  subscribeToCompletedUserActionItems(
    {getState, patchState}: StateContext<UserActionItemsStateModel>,
    { lenderUser }: SubscribeToCompletedUserActionItems
  ) {
    this.pusherService
      .subscribeRaw(`private-${environment.environment}-lender-user-${lenderUser.id}-today-tab-channel`)
      .bind_global((eventName: string, data: any) => {
        if (eventName.startsWith('userActionItemCompleted') && !!data?.id) {
          //Let's remove completed items from state
          const currentItems = getState().userActionItems;
          const newUserActionItems = currentItems.filter(item => {
            if (item.id !== data.id) {
              return item;
            }
          });

          this.activeActionItem$.pipe(take(1)).subscribe(currentItem => {
            if (!currentItem) {
              return;
            }

            //If the current active item is the completed item, let's load up the next actionItem
            if (currentItem.id === data.id) {
              //Unset the previously selected userActionItem details
              this.store.dispatch(new UnsetActiveUserActionItem());
              const firstActionItem = newUserActionItems[0] || null;
              //Select the next available UserActionItem if available
              if (firstActionItem) {
                this.store.dispatch( new GetUserActionItemDetails(firstActionItem.id));
              }
            }
          });

          // Refresh the TodayBadgeCount
          this.store.dispatch(new GetTodayBadgeCounts(lenderUser.id));

          patchState({
            userActionItems: newUserActionItems
          });
        }
      });
  }

  @Action(MarkActionItemCompleted)
  markActionItemCompleted(
    { }: StateContext<UserActionItemsStateModel>,
    { id }: MarkActionItemCompleted
  ) {
    if(!!id) {
      return this.actionItemsService.markActionItemComplete(id);
    }
  }

  /**
   * Checks for additional kickback requests for the borrowerId argument.
   * Determiner for auto re-submission of the borrower application to Funding Desk.
   * Similar purpose as workflow-tasks.state.ts multipleExistingKickbacksForBorrower()
   *
   * @param userActionItems
   * @param borrowerId
   * @returns number
   */
  private multipleExistingKickbacksForBorrower(userActionItems: UserActionItem[], borrowerId: number): number {

    const count = userActionItems.filter((uai) => (uai.type?.toLowerCase() === 'kickback') && (uai.borrowerId === borrowerId) && (!uai.completed));

    return count.length ?? 0;
  }
}
